import { useState, useEffect } from 'react';
import { HiOutlineX, HiOutlineFilter } from 'react-icons/hi';
import { HiOutlineDocumentText, HiOutlineTag, HiOutlineCalendarDays } from 'react-icons/hi2';
import CustomDropdownWithSearch from '../CustomDropdownWithSearch/CustomDropdownWithSearch';
import { fetchAllRetailers } from '../../services/mapPageService';
import type { BrandFileFilters } from '../../services/brandFilesService';
import './BrandFilesFilter.css';

interface BrandFilesFilterProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (filters: BrandFileFilters) => void;
  showBrandFilter?: boolean;
  appliedFilters?: BrandFileFilters;
}

export default function BrandFilesFilter({ isOpen, onClose, onApplyFilters, showBrandFilter = false, appliedFilters }: BrandFilesFilterProps) {
  const [brandName, setBrandName] = useState<string[]>([]);
  const [availableBrands, setAvailableBrands] = useState<string[]>([]);
  const [name, setName] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    if (showBrandFilter) {
      fetchAllRetailers().then(setAvailableBrands);
    }
  }, [showBrandFilter]);

  // Sync local state with applied filters when panel opens
  useEffect(() => {
    if (isOpen && appliedFilters) {
      setBrandName(appliedFilters.brandNames || []);
      setName(appliedFilters.name || '');
      setDateFrom(appliedFilters.dateFrom || '');
      setDateTo(appliedFilters.dateTo || '');
    }
  }, [isOpen, appliedFilters]);

  const activeFilterCount =
    (showBrandFilter && brandName.length > 0 ? 1 : 0) +
    (name ? 1 : 0) +
    (dateFrom ? 1 : 0) +
    (dateTo ? 1 : 0);

  const handleClearAll = () => {
    setBrandName([]);
    setName('');
    setDateFrom('');
    setDateTo('');
    onApplyFilters({});
  };

  const handleApply = () => {
    const filters: BrandFileFilters = {};
    if (showBrandFilter && brandName.length > 0) filters.brandNames = brandName;
    if (name) filters.name = name;
    if (dateFrom) filters.dateFrom = dateFrom;
    if (dateTo) filters.dateTo = dateTo;
    onApplyFilters(filters);
  };

  return (
    <>
      {isOpen && <div className="bff-overlay" onClick={onClose}></div>}
      <div className={`bff-panel ${isOpen ? 'open' : ''}`}>
        <div className="bff-header">
          <div className="bff-header-left">
            <div className="bff-header-icon">
              <HiOutlineFilter />
            </div>
            <div className="bff-header-text">
              <h2>Filters</h2>
              <p>Filter files by attributes</p>
            </div>
            {activeFilterCount > 0 && (
              <span className="bff-badge">{activeFilterCount}</span>
            )}
          </div>
          <button className="bff-close-btn" onClick={onClose}>
            <HiOutlineX />
          </button>
        </div>

        {activeFilterCount > 0 && (
          <div className="bff-clear-section">
            <button className="bff-clear-btn" onClick={handleClearAll}>
              <HiOutlineX />
              Clear All Filters
            </button>
          </div>
        )}

        <div className="bff-content">
          <div className="bff-scroll">
            {/* Active Filter Chips */}
            {activeFilterCount > 0 && (
              <div className="bff-active-display">
                <div className="bff-active-title">Active Filters</div>
                <div className="bff-active-list">
                  {showBrandFilter && brandName.length > 0 && (
                    <span className="bff-chip">
                      Brands: {brandName.join(', ')}
                      <button className="bff-chip-remove" onClick={() => setBrandName([])}><HiOutlineX /></button>
                    </span>
                  )}
                  {name && (
                    <span className="bff-chip">
                      Name: {name}
                      <button className="bff-chip-remove" onClick={() => setName('')}><HiOutlineX /></button>
                    </span>
                  )}
                  {dateFrom && (
                    <span className="bff-chip">
                      From: {dateFrom}
                      <button className="bff-chip-remove" onClick={() => setDateFrom('')}><HiOutlineX /></button>
                    </span>
                  )}
                  {dateTo && (
                    <span className="bff-chip">
                      To: {dateTo}
                      <button className="bff-chip-remove" onClick={() => setDateTo('')}><HiOutlineX /></button>
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Brand Filter (only for AllFiles page) */}
            {showBrandFilter && (
              <div className="bff-group">
                <div className="bff-group-header">
                  <HiOutlineTag className="bff-group-icon" />
                  <span className="bff-group-title">Brand</span>
                  {brandName.length > 0 ? <span className="bff-group-count">{brandName.length}</span> : null}
                </div>
                <div className="bff-group-body">
                  <div className="bff-section">
                    <div className="bff-section-header">
                      <h3>Brand Name</h3>
                    </div>
                    <div className="bff-fields">
                      <CustomDropdownWithSearch
                        value={brandName}
                        options={availableBrands}
                        onChange={setBrandName}
                        placeholder="Select brands..."
                        showTags={false}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* File Name Filter */}
            <div className="bff-group">
              <div className="bff-group-header">
                <HiOutlineDocumentText className="bff-group-icon" />
                <span className="bff-group-title">File Name</span>
                {name ? <span className="bff-group-count">1</span> : null}
              </div>
              <div className="bff-group-body">
                <div className="bff-section">
                  <div className="bff-section-header">
                    <h3>Name Contains</h3>
                  </div>
                  <div className="bff-fields">
                    <input
                      type="text"
                      className="bff-text-input"
                      placeholder="Search file name..."
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Date Range Filter */}
            <div className="bff-group">
              <div className="bff-group-header">
                <HiOutlineCalendarDays className="bff-group-icon" />
                <span className="bff-group-title">Date Range</span>
                {(dateFrom || dateTo) ? <span className="bff-group-count">{(dateFrom ? 1 : 0) + (dateTo ? 1 : 0)}</span> : null}
              </div>
              <div className="bff-group-body">
                <div className="bff-section">
                  <div className="bff-section-header">
                    <h3>From Date</h3>
                  </div>
                  <div className="bff-fields">
                    <input
                      type="date"
                      className="bff-date-input"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                    />
                  </div>
                </div>
                <div className="bff-section">
                  <div className="bff-section-header">
                    <h3>To Date</h3>
                  </div>
                  <div className="bff-fields">
                    <input
                      type="date"
                      className="bff-date-input"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="bff-actions">
            <button className="bff-action-close" onClick={onClose}>Close</button>
            <button className="bff-action-apply" onClick={handleApply}>Apply Filters</button>
          </div>
        </div>
      </div>
    </>
  );
}
